import { useAuth, API, isValidUser } from "@/App";
import PageSeo from "@/components/seo/PageSeo";
import SeoImage from "@/components/seo/SeoImage";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion, useInView } from "framer-motion";
import RobotEyeHero from '../components/hero/RobotEyeHero';
import PlatformForEveryone from '../components/platform/PlatformForEveryone';
import StudentOfferingsCarousel from '../components/landing/StudentOfferingsCarousel';
import Lenis from "lenis";
import { asArray } from "@/lib/arrays";
import {
  TrendingUp, BookOpen, Users, Zap, Star, Play, ArrowRight,
  Shield, Target, BarChart3, Brain, Flame, AlertTriangle, DollarSign,
  Ban, Newspaper, CreditCard, TrendingDown, Eye, ChevronRight, ChevronLeft,
  Monitor, Rocket, GraduationCap, Phone, Mail, MapPin, Instagram,
  Facebook, Linkedin, ChevronDown, CheckCircle, XCircle, Crosshair,
  Youtube, Menu, X, Activity, LineChart, Sparkles
} from "lucide-react";

const TELEGRAM_LINK = "https://t.me/+QOePMys7V481NmZl";
const HERO_BG = "/hero-bg.webp";

/* ─── Reveal on scroll ─── */
const Reveal = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/* ─── Animated candlestick SVG background ─── */
const CandlestickBg = ({ opacity = 0.04 }) => (
  <svg className="landing-market-lines absolute inset-0 w-full h-full pointer-events-none hidden lg:block" style={{ opacity }} viewBox="0 0 1200 600" preserveAspectRatio="xMidYMid slice">
    {[100, 200, 300, 400, 500, 600, 700, 800, 900, 1000, 1100].map((x, i) => {
      const h = 40 + Math.sin(i * 1.5) * 80;
      const y = 300 - h / 2;
      const isGreen = i % 3 !== 0;
      return (
        <g key={i}>
          <line x1={x} y1={y - 20} x2={x} y2={y + h + 20} stroke={isGreen ? "#00FF88" : "#FF3B30"} strokeWidth="1" />
          <rect x={x - 8} y={y} width="16" height={h} fill={isGreen ? "#00FF88" : "#FF3B30"} rx="1" />
        </g>
      );
    })}
    {/* Grid lines */}
    {[100, 200, 300, 400, 500].map(y => <line key={y} x1="0" y1={y} x2="1200" y2={y} stroke="#00F0FF" strokeWidth="0.3" strokeDasharray="8 8" />)}
  </svg>
);

/* ─── Floating particles ─── */
const Particles = () => (
  <div className="landing-particles absolute inset-0 overflow-hidden pointer-events-none hidden md:block">
    {Array.from({ length: 12 }).map((_, i) => (
      <div key={i} className="absolute rounded-full" style={{
        width: `${2 + (i % 3)}px`, height: `${2 + (i % 3)}px`,
        background: i % 2 === 0 ? '#00FF88' : '#00F0FF',
        opacity: 0.12,
        left: `${(i * 23) % 100}%`, top: `${(i * 37) % 100}%`,
        animation: `float ${9 + (i % 4)}s ease-in-out infinite`,
        animationDelay: `${(i % 5) * 0.6}s`
      }} />
    ))}
  </div>
);

/* ─── FAQ Accordion Item ─── */
const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="terminal-card rounded-lg overflow-hidden border-white/[0.04] hover:border-white/[0.08] transition-colors" data-testid="faq-item">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-5 text-left">
        <span className="text-base font-semibold text-white font-outfit pr-4">{q}</span>
        <ChevronDown className={`w-5 h-5 text-[#00FF88] shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}`}>
        <p className="px-5 pb-5 text-sm text-[#CBD5E1] font-plex leading-relaxed">{a}</p>
      </div>
    </div>
  );
};

const AnimatedCounter = ({ value, label }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const strValue = String(value || "");
  const numValue = parseInt(strValue.replace(/[^0-9]/g, '')) || 0;
  const suffix = strValue.replace(/[0-9]/g, '');
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (isInView) {
      let start = 0;
      const duration = 2000;
      const startTime = performance.now();

      const updateCounter = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const ease = 1 - Math.pow(1 - progress, 4);
        setCount(Math.floor(ease * numValue));
        if (progress < 1) {
          requestAnimationFrame(updateCounter);
        } else {
          setCount(numValue);
        }
      };
      requestAnimationFrame(updateCounter);
    }
  }, [isInView, numValue]);

  return (
    <div ref={ref} className="text-center">
      <p className="text-2xl sm:text-3xl font-bold text-white font-jetbrains">
        {count}{suffix}
      </p>
      <p className="text-[10px] sm:text-xs text-[#94A3B8] font-jetbrains tracking-[0.15em] uppercase mt-1">{label}</p>
    </div>
  );
};

const CourseCarousel = ({ courses = [] }) => {
  const fallbackCourses = [
    {
      course_id: "smart-trader-elementary",
      title: "Smart Trader Elementary Course",
      description: "Start your trading journey with market basics, structure, and risk-first thinking.",
      price_display: "FREE",
      is_free: true,
      sessions: 8,
      thumbnail: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=900&auto=format&fit=crop",
    },
    {
      course_id: "technical-analysis-course",
      slug: "technical-analysis-course",
      title: "Super Trader Edge Course",
      description: "Master demand and supply zones, price action, entries, exits, and trade management.",
      price_display: "₹15,000",
      price: 15000,
      sessions: 25,
      thumbnail: "https://images.unsplash.com/photo-1642790106117-e829e14a795f?w=900&auto=format&fit=crop",
    },
    {
      course_id: "options-trading-course",
      slug: "options-trading-course",
      title: "Super Option Analysis Course",
      description: "Learn option chain reading, Greeks, strategy selection, and execution discipline.",
      price_display: "₹20,000",
      price: 20000,
      sessions: 10,
      thumbnail: "https://images.unsplash.com/photo-1639762681057-408e52192e55?w=900&auto=format&fit=crop",
    },
    {
      course_id: "technical-analysis-options-combo-course",
      slug: "technical-analysis-options-combo-course",
      title: "Super Trader Combo",
      description: "The complete package for traders who want structured mastery across stocks and options.",
      price_display: "₹25,000",
      price: 25000,
      sessions: 35,
      thumbnail: "https://images.unsplash.com/photo-1610664921899-7d073a3f6dfc?w=900&auto=format&fit=crop",
    },
  ];
  const items = courses.length ? courses : fallbackCourses;
  const [activeIndex, setActiveIndex] = useState(Math.min(1, items.length - 1));

  useEffect(() => {
    if (!items.length) return;
    setActiveIndex((current) => Math.min(current, items.length - 1));
  }, [items.length]);

  const move = (direction) => {
    setActiveIndex((current) => (current + direction + items.length) % items.length);
  };

  const getOffset = (index) => {
    const raw = index - activeIndex;
    const half = items.length / 2;
    if (raw > half) return raw - items.length;
    if (raw < -half) return raw + items.length;
    return raw;
  };

  const getCourseUrl = (course) => `/courses/${course.slug || course.course_id}`;

  return (
    <div className="relative" data-testid="course-carousel">
      <div className="absolute inset-x-0 top-14 mx-auto h-72 max-w-4xl rounded-full bg-[radial-gradient(circle,rgba(0,255,136,0.16),rgba(0,240,255,0.05)_38%,transparent_70%)] blur-2xl" />
      <div className="absolute inset-x-8 top-24 h-52 rounded-full bg-[radial-gradient(circle,rgba(255,59,48,0.12),transparent_64%)] blur-3xl" />

      <div className="relative hidden min-h-[470px] items-center justify-center overflow-hidden md:flex">
        <button
          type="button"
          onClick={() => move(-1)}
          className="absolute left-0 z-30 flex h-12 w-12 items-center justify-center rounded-full border border-white/[0.08] bg-black/55 text-white backdrop-blur-xl transition-all hover:-translate-x-1 hover:border-[#00FF88]/50 hover:bg-[#00FF88] hover:text-black hover:shadow-[0_0_28px_rgba(0,255,136,0.24)]"
          aria-label="Previous course"
          data-testid="course-carousel-prev"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={() => move(1)}
          className="absolute right-0 z-30 flex h-12 w-12 items-center justify-center rounded-full border border-white/[0.08] bg-black/55 text-white backdrop-blur-xl transition-all hover:translate-x-1 hover:border-[#00FF88]/50 hover:bg-[#00FF88] hover:text-black hover:shadow-[0_0_28px_rgba(0,255,136,0.24)]"
          aria-label="Next course"
          data-testid="course-carousel-next"
        >
          <ChevronRight className="h-5 w-5" />
        </button>

        <div className="relative h-[430px] w-full max-w-6xl">
          {items.map((course, index) => {
            const offset = getOffset(index);
            const isActive = offset === 0;
            const isVisible = Math.abs(offset) <= 2;
            return (
              <motion.div
                key={course.course_id || index}
                className="absolute left-1/2 top-1/2 w-[300px] lg:w-[340px]"
                animate={{
                  x: `calc(-50% + ${offset * 245}px)`,
                  y: isActive ? "-54%" : "-48%",
                  scale: isActive ? 1.08 : 0.82 - Math.min(Math.abs(offset), 2) * 0.08,
                  rotateY: offset * -12,
                  opacity: isVisible ? (isActive ? 1 : 0.42) : 0,
                  zIndex: 20 - Math.abs(offset),
                  filter: isActive ? "blur(0px)" : "blur(1.2px)",
                }}
                transition={{ type: "spring", stiffness: 160, damping: 24, mass: 0.8 }}
                style={{ pointerEvents: isVisible ? "auto" : "none", transformStyle: "preserve-3d" }}
              >
                <Link
                  to={getCourseUrl(course)}
                  className={`group block overflow-hidden rounded-[18px] border bg-[#080A0C]/90 shadow-2xl backdrop-blur-xl transition-all duration-300 ${isActive ? "border-[#00FF88]/45 shadow-[0_0_45px_rgba(0,255,136,0.16)]" : "border-white/[0.08] hover:border-[#00F0FF]/35"}`}
                  data-testid={`course-card-${index}`}
                >
                  <div className="relative h-[360px] overflow-hidden">
                    <img
                      src={course.thumbnail || "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=900&auto=format&fit=crop"}
                      alt={course.title}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/28 to-transparent" />
                    <div className="absolute inset-0 bg-[linear-gradient(115deg,transparent,rgba(255,255,255,0.16),transparent)] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                    <div className="absolute left-4 top-4 flex items-center gap-2">
                      <Badge className={`${course.is_free ? "bg-[#00FF88]/95 text-black" : "bg-[#00F0FF]/90 text-black"} border-0 rounded-sm text-[10px] font-jetbrains font-bold`}>
                        {course.is_free ? "FREE" : `${course.sessions || 0} SESSIONS`}
                      </Badge>
                    </div>
                    <div className="absolute right-4 top-4 rounded-full border border-white/15 bg-black/40 px-3 py-1 font-jetbrains text-sm font-bold text-white backdrop-blur">
                      {String(index + 1).padStart(2, "0")}
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <p className="mb-2 font-jetbrains text-[10px] uppercase tracking-[0.2em] text-[#00F0FF]">Trading System</p>
                      <h3 className="font-outfit text-xl font-bold leading-tight text-white">{course.title}</h3>
                      <p className="mt-2 line-clamp-2 min-h-[40px] text-sm leading-5 text-[#CBD5E1]">{course.description}</p>
                      <div className="mt-4 flex items-center justify-between border-t border-white/[0.08] pt-4">
                        <span className={`font-jetbrains text-lg font-bold ${course.is_free ? "text-[#00FF88]" : "text-white"}`}>
                          {course.price_display || `₹${course.price?.toLocaleString("en-IN")}`}
                        </span>
                        <span className="flex items-center gap-1 text-xs font-bold text-[#00FF88] transition-all group-hover:gap-2">
                          Explore <ArrowRight className="h-3.5 w-3.5" />
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>

      <div className="relative md:hidden">
        <div className="-mx-4 flex snap-x gap-4 overflow-x-auto px-4 pb-4">
          {items.map((course, index) => (
            <Link
              key={course.course_id || index}
              to={getCourseUrl(course)}
              className="tracing-card block w-[82vw] shrink-0 snap-center overflow-hidden rounded-lg"
              data-testid={`mobile-course-card-${index}`}
            >
              <div className="relative h-52 overflow-hidden">
                <img src={course.thumbnail || "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=900&auto=format&fit=crop"} alt={course.title} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent" />
              </div>
              <div className="p-5">
                <Badge className={`${course.is_free ? "bg-[#00FF88]/15 text-[#00FF88]" : "bg-[#00F0FF]/15 text-[#00F0FF]"} border-0 rounded-sm text-xs font-jetbrains`}>
                  {course.is_free ? "FREE" : `${course.sessions || 0} SESSIONS`}
                </Badge>
                <h3 className="mt-3 font-outfit text-lg font-bold text-white">{course.title}</h3>
                <p className="mt-2 line-clamp-2 text-sm text-[#94A3B8]">{course.description}</p>
                <div className="mt-4 flex items-center justify-between border-t border-white/5 pt-3">
                  <span className={`font-jetbrains text-base font-bold ${course.is_free ? "text-[#00FF88]" : "text-white"}`}>{course.price_display || `₹${course.price?.toLocaleString("en-IN")}`}</span>
                  <ArrowRight className="h-4 w-4 text-[#00FF88]" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div className="mt-2 flex items-center justify-center gap-2 md:mt-0">
        {items.map((course, index) => (
          <button
            key={course.course_id || index}
            type="button"
            onClick={() => setActiveIndex(index)}
            className={`h-1.5 rounded-full transition-all ${activeIndex === index ? "w-8 bg-[#00FF88] shadow-[0_0_14px_rgba(0,255,136,0.6)]" : "w-2 bg-white/20 hover:bg-white/40"}`}
            aria-label={`Show course ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

/* ─── Video review card with auto-play on hover ─── */
const VideoCard = ({ videoSrc, studentName, studentThumb }) => {
  const videoRef = useRef(null);

  const handleMouseEnter = () => {
    if (videoRef.current) {
      videoRef.current.muted = false; // Attempt unmuted playback
      videoRef.current.play().catch(() => {
        // If browser blocks unmuted autoplay, fallback to muted playback
        if (videoRef.current) {
          videoRef.current.muted = true;
          videoRef.current.play().catch(() => { });
        }
      });
    }
  };

  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  return (
    <div
      className="video-review-card relative overflow-hidden rounded-xl border border-purple-500/20 bg-black/60 p-4 transition-all duration-300 hover:border-purple-500/50 hover:scale-[1.07] hover:z-10 group cursor-pointer"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Video element */}
      <video
        ref={videoRef}
        src={videoSrc}
        playsInline
        loop
        muted
        className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
      />

      {/* Overlay contents */}
      <div className="relative z-10 flex flex-col h-full justify-between pointer-events-none transition-opacity duration-300 group-hover:opacity-0">
        {/* Jama Dhan Logo at top */}
        <div className="flex justify-between items-center">
          <img src="/jamadhan-logo.png" alt="Jama Dhan Logo" className="h-4 object-contain" />
          <span className="text-[9px] font-bold font-jetbrains text-purple-400 bg-purple-950/50 px-2 py-0.5 rounded border border-purple-500/30">VIDEO REVIEW</span>
        </div>

        {/* Circular student photo in center */}
        <div className="flex flex-col items-center justify-center my-3 group-hover:scale-95 transition-transform duration-300">
          <img
            src={studentThumb}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(studentName)}&backgroundColor=000000&textColor=00FF88`;
              e.target.style.objectFit = 'cover';
              e.target.style.padding = '0';
              e.target.style.backgroundColor = 'transparent';
            }}
            alt={studentName}
            className="w-16 h-16 rounded-full object-cover border-2 border-purple-500/50 shadow-[0_0_15px_rgba(168,85,247,0.4)]"
          />
          <h4 className="text-xs font-semibold text-white font-outfit mt-1.5">{studentName}</h4>
        </div>

        {/* "Student Reviews" label & 5 star rating at bottom */}
        <div className="flex flex-col items-center">
          <span className="text-[9px] font-bold text-gray-400 font-jetbrains tracking-widest uppercase">Student Review</span>
          <div className="flex gap-0.5 mt-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} className="w-3 h-3 text-[#FFCC00] fill-[#FFCC00]" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const LandingPage = () => {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [courses, setCourses] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [blogs, setBlogs] = useState([]);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [activeFaq, setActiveFaq] = useState(null);

  useEffect(() => {
    if (!loading && isValidUser(user)) {
      const routes = { admin: '/admin', mentor: '/mentor', counselor: '/counselor' };
      navigate(routes[user.role] || '/dashboard');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  // Initialize Lenis smooth scroll specifically for landing page hero
  useEffect(() => {
    let lenis;
    let rafId;
    try {
      lenis = new Lenis({
        duration: 1.2,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smooth: true,
        smoothTouch: false,
        allowNestedScroll: true,
        prevent: (node) => {
          if (!node || !(node instanceof HTMLElement)) return false;
          return (
            node.hasAttribute?.('data-lenis-prevent') ||
            node.closest?.('[data-lenis-prevent]') !== null ||
            node.closest?.('[role="dialog"]') !== null ||
            node.closest?.('[data-radix-portal]') !== null ||
            node.closest?.('.custom-scrollbar') !== null
          );
        }
      });
      window.__lenis = lenis;

      function raf(time) {
        lenis?.raf(time);
        rafId = requestAnimationFrame(raf);
      }
      rafId = requestAnimationFrame(raf);
    } catch (err) {
      console.warn("Lenis init skipped:", err);
    }

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (lenis) lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  // Handle hash-based scroll (e.g. /#courses, /#reviews) on page load and location change
  useEffect(() => {
    const hash = location.hash?.replace('#', '');
    if (hash) {
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location.hash]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/courses`), axios.get(`${API}/reviews`), axios.get(`${API}/blogs`),
      axios.get(`${API}/site-config`)
    ]).then(([c, r, b, s]) => {
      setCourses(asArray(c.data, "courses"));
      setReviews(asArray(r.data, "reviews"));
      setBlogs(asArray(b.data, "blogs"));
      setCfg(s.data);
    }).catch(() => { });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Use cfg values or defaults
  const SC = cfg || {};
  const TELE = SC.telegram_link || TELEGRAM_LINK;

  const scrollTo = (id) => { setMobileMenu(false); document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); };

  const navLinks = [
    { label: "Home", href: "/" },
    { label: "About", href: "/about" },
    { label: "Courses", href: "/#courses" },
    { label: "Blogs", href: "/blog" },
    { label: "Gallery", href: "/gallery" },
    { label: "Contact", href: "/contact" }
  ];

  const problems = [
    { icon: Flame, label: "Overtrading", desc: "Taking too many trades without a plan" },
    { icon: TrendingDown, label: "Revenge Trading", desc: "Emotional trading after a loss" },
    { icon: Eye, label: "FOMO", desc: "Fear of missing out on every move" },
    { icon: DollarSign, label: "Penny Stocks", desc: "Gambling on low-quality stocks" },
    { icon: Ban, label: "No Stop Loss", desc: "Holding losers hoping for a bounce" },
    { icon: Newspaper, label: "News Based Trading", desc: "Reacting to headlines, not charts" },
    { icon: CreditCard, label: "Loan / Over Leverage", desc: "Trading with borrowed money" }
  ];

  const whyUs = [
    { icon: Target, title: "Institutional Trading Style", desc: "Learn how smart money trades — Demand & Supply zones used by institutions" },
    { icon: Shield, title: "Risk Management", desc: "Never risk more than you can afford. Position sizing & capital protection" },
    { icon: BarChart3, title: "Trade Management", desc: "Entry, exit, trailing stop loss — manage every trade like a pro" },
    { icon: Brain, title: "Secret Strategies", desc: "Proprietary setups that work in Indian markets — not available anywhere else" },
    { icon: Monitor, title: "Live Market Training", desc: "Real-time practice on live charts with Deepak Sir during market hours" },
    { icon: Zap, title: "Drishti AI Tool", desc: "An AI-powered scanner that screens 500+ stocks for chart patterns and technical conditions, so you can practise reading the market yourself." }
  ];

  const liveOpportunities = [
    { title: "Stocks Near Demand Zone", count: "12", color: "#00FF88", icon: TrendingUp, desc: "Ready to bounce" },
    { title: "Stocks Near Supply Zone", count: "8", color: "#FF3B30", icon: TrendingDown, desc: "Potential reversal" },
    { title: "Ready Trade Setups", count: "5", color: "#00F0FF", icon: Crosshair, desc: "Actionable now" },
    { title: "High Probability Trades", count: "3", color: "#FFCC00", icon: Activity, desc: "Best risk-reward" }
  ];

  const timeline = [
    { year: "2019", title: "The Beginning", desc: "Started learning trading, faced massive losses. Understood the need for structured education." },
    { year: "2021", title: "First Batch", desc: "Taught the first batch of 20 students. 80% became profitable within 6 months." },
    { year: "2023", title: "Jamadhan Born", desc: "Founded Jamadhan as a full ecosystem — courses, AI tools, and community." },
    { year: "2025", title: "10000+ Students", desc: "Crossed 10000 trained traders. Launched Drishti AI for automated market scanning." }
  ];
  const localBusinessSchema = SC.local_seo_business_name ? {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": SC.local_seo_business_name,
    "image": SC.global_og_image || "https://jamadhan.com/jamadhan-logo.png",
    "@id": "https://jamadhan.com",
    "url": "https://jamadhan.com",
    "telephone": SC.local_seo_phone,
    "email": SC.local_seo_email,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": SC.local_seo_street,
      "addressLocality": SC.local_seo_city,
      "addressRegion": SC.local_seo_region,
      "postalCode": SC.local_seo_postal,
      "addressCountry": SC.local_seo_country
    },
    "geo": SC.local_seo_latitude ? {
      "@type": "GeoCoordinates",
      "latitude": SC.local_seo_latitude,
      "longitude": SC.local_seo_longitude
    } : undefined,
    "openingHours": SC.local_seo_opening_hours,
    "priceRange": SC.local_seo_price_range || "₹₹",
    "sameAs": [
      SC.social_facebook,
      SC.social_linkedin,
      SC.instagram_link,
      SC.youtube_link
    ].filter(Boolean)
  } : null;

  // If user is already authenticated or holds an active session token, do not flash the public landing page while redirecting
  const hasSessionToken = typeof window !== "undefined" && Boolean(window.localStorage?.getItem("jamadhan_session_token"));
  if (hasSessionToken && (loading || isValidUser(user))) {
    return <div className="min-h-screen bg-[#05070E]" />;
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white selection:bg-[#00FF88]/30 selection:text-white" data-testid="landing-page">
      <PageSeo
        path="/"
        fallbackTitle={SC.home_seo_title || "Jamadhan | See The Market Before It Moves"}
        fallbackDescription={SC.home_seo_description || "Master technical analysis, learn from real trades, and get AI-powered demand-supply zone alerts."}
      />
      {localBusinessSchema && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
        </Helmet>
      )}
      {/* ═══ HEADER ═══ */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#050505]/90 backdrop-blur-xl border-b border-white/5' : 'bg-transparent'}`} data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded overflow-hidden shadow-[0_0_20px_rgba(0,255,136,0.3)]">
              <img src="/jamadhan-logo.png" alt="Jamadhan" className="w-full h-full object-cover" />
            </div>
            <span className="text-lg font-bold font-outfit tracking-tight text-white">JAMADHAN</span>
          </div>
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map(l => l.href ? (
              l.href.startsWith('/#') ? (
                <button key={l.label}
                  onClick={() => {
                    const id = l.href.replace('/#', '');
                    const el = document.getElementById(id);
                    if (el) { el.scrollIntoView({ behavior: 'smooth' }); }
                    else { window.location.href = l.href; }
                  }}
                  className="text-sm text-[#CBD5E1] hover:text-[#00FF88] transition-colors font-medium tracking-wide">
                  {l.label}
                </button>
              ) : (
                <Link key={l.label} to={l.href} className="text-sm text-[#CBD5E1] hover:text-[#00FF88] transition-colors font-medium tracking-wide">{l.label}</Link>
              )
            ) : (
              <button key={l.label} onClick={() => scrollTo(l.id)} className="text-sm text-[#CBD5E1] hover:text-[#00FF88] transition-colors font-medium tracking-wide">{l.label}</button>
            ))}
            <Link to="/drishti-ai" className="text-sm text-[#00F0FF] hover:text-[#00FF88] font-semibold transition-colors">Drishti AI</Link>

            <Button onClick={() => navigate('/login')} size="sm" className="bg-[#00FF88] text-black font-bold hover:bg-black hover:text-white hover:shadow-[0_0_20px_rgba(0,255,136,0.3)] border border-transparent hover:border-[#00FF88] rounded-sm px-5 transition-all duration-200" data-testid="login-btn">
              Login
            </Button>
          </div>
          <button onClick={() => setMobileMenu(!mobileMenu)} className="md:hidden text-white" data-testid="mobile-menu-btn">
            {mobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {mobileMenu && (
          <div className="md:hidden bg-[#050505]/98 border-t border-white/5 text-white backdrop-blur-xl px-6 pb-4">
            {navLinks.map(l => l.href ? (
              l.href.startsWith('/#') ? (
                <button key={l.label}
                  onClick={() => {
                    setMobileMenu(false);
                    const id = l.href.replace('/#', '');
                    const el = document.getElementById(id);
                    if (el) { el.scrollIntoView({ behavior: 'smooth' }); }
                    else { window.location.href = l.href; }
                  }}
                  className="block w-full text-left py-3 text-[#CBD5E1] hover:text-[#00FF88] border-b border-white/5">
                  {l.label}
                </button>
              ) : (
                <Link key={l.label} to={l.href} className="block py-3 text-[#CBD5E1] hover:text-[#00FF88] border-b border-white/5">{l.label}</Link>
              )
            ) : (
              <button key={l.label} onClick={() => scrollTo(l.id)} className="block w-full text-left py-3 text-[#CBD5E1] hover:text-[#00FF88] border-b border-white/5">{l.label}</button>
            ))}
            <Link to="/drishti-ai" className="block py-3 text-[#00F0FF] hover:text-[#00FF88] border-b border-white/5 font-semibold">Drishti AI</Link>

            <Button onClick={() => navigate('/login')} className="mt-3 w-full bg-[#00FF88] text-black font-bold hover:bg-black hover:text-white hover:border-[#00FF88] border border-transparent transition-all duration-200">Login</Button>
          </div>
        )}
      </nav>

      {/* ═══ ROBOT EYE HERO ═══ */}
      {/* Fixed overlay — body scroll locked while assembling, released on completion */}
      <RobotEyeHero />

      {/* ═══ AS FEATURED IN SECTION ═══ */}
      <section className="py-12 px-4 sm:px-6 relative overflow-hidden" data-testid="featured-in-section">
        <style dangerouslySetInnerHTML={{
          __html: `
          @keyframes featuredMarquee {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .featured-marquee-content {
            display: flex;
            gap: 2rem;
            width: max-content;
            animation: featuredMarquee 35s linear infinite;
          }
          .featured-marquee-content:hover {
            animation-play-state: paused;
          }
          .featured-overlay-container {
            position: relative;
            width: 100%;
            overflow: hidden;
          }
          .featured-blur-left {
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 25%;
            z-index: 10;
            pointer-events: none;
            background: linear-gradient(to right, rgba(5, 5, 5, 0.95), rgba(5, 5, 5, 0.5) 40%, transparent);
            backdrop-filter: blur(12px);
            -webkit-backdrop-filter: blur(12px);
            mask-image: linear-gradient(to right, black, transparent);
            -webkit-mask-image: linear-gradient(to right, black, transparent);
          }
          .featured-blur-right {
            position: absolute;
            right: 0;
            top: 0;
            bottom: 0;
            width: 25%;
            z-index: 10;
            pointer-events: none;
            background: linear-gradient(to left, rgba(5, 5, 5, 0.95), rgba(5, 5, 5, 0.5) 40%, transparent);
            backdrop-filter: blur(12px);
            -webkit-backdrop-filter: blur(12px);
            mask-image: linear-gradient(to left, black, transparent);
            -webkit-mask-image: linear-gradient(to left, black, transparent);
          }
        `}} />
        <div className="max-w-7xl mx-auto relative z-10">
          <Reveal className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-white font-outfit">As Featured In</h2>
          </Reveal>

          <div className="featured-overlay-container py-4">
            <div className="featured-blur-left" />
            <div className="featured-blur-right" />

            <div className="featured-marquee-content">
              {[
                "/media__1780523733874.jpg",
                "/media__1780523733879.jpg",
                "/media__1780523733890.jpg",
                "/media__1780523733903.jpg",
                "/media__1780523733905.jpg",
                "/media__1780523733874.jpg",
                "/media__1780523733879.jpg",
                "/media__1780523733890.jpg",
                "/media__1780523733903.jpg",
                "/media__1780523733905.jpg"
              ].map((img, idx) => (
                <div
                  key={idx}
                  className="w-[200px] sm:w-[260px] flex-shrink-0 bg-[#0C0C10] rounded-lg border border-white/[0.06] overflow-hidden shadow-[0_4px_16px_rgba(0,0,0,0.5)] p-1.5 transition-all duration-300 hover:border-white/10"
                >
                  <img
                    src={img}
                    alt="Featured news publication"
                    className="w-full h-auto rounded-md object-contain"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ A PLATFORM FOR EVERYONE SECTION ═══ */}
      <PlatformForEveryone />

      {/* ═══ PROBLEM SECTION ═══ */}
      <section className="py-14 sm:py-20 px-4 sm:px-6 relative" data-testid="problems-section">
        <CandlestickBg opacity={0.03} />
        <div className="max-w-7xl mx-auto relative z-10">
          <Reveal className="text-center mb-12">
            <p className="font-jetbrains text-xs tracking-[0.25em] uppercase text-[#FF3B30] mb-3">STOP LOSING MONEY</p>
            <h2 className="text-4xl sm:text-5xl font-bold text-white font-outfit mb-4">Are You Facing These Problems?</h2>
            <p className="text-[#CBD5E1] max-w-xl mx-auto text-lg font-plex">90% of traders lose money because of these mistakes. Jamadhan fixes every single one.</p>
          </Reveal>
          <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 max-w-5xl mx-auto">
            {problems.map((p, i) => (
              <Reveal key={p.label} delay={i * 0.05} className="h-full">
                <div className="terminal-card rounded-lg p-4 sm:p-5 group hover:border-[#00FF88]/30 transition-all duration-300 cursor-default h-full flex flex-col" data-testid={`problem-card-${i}`}>
                  <div className="w-10 h-10 rounded-lg bg-[#FF3B30]/10 flex items-center justify-center mb-3 group-hover:bg-[#00FF88]/10 transition-colors">
                    <p.icon className="w-5 h-5 text-[#FF3B30] group-hover:text-[#00FF88] transition-colors" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-base font-semibold text-white font-outfit mb-1">{p.label}</h3>
                  <p className="text-sm text-[#94A3B8] font-plex leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
            {/* Solution card */}
            <Reveal delay={0.4} className="h-full">
              <div className="rounded-lg p-4 sm:p-5 border border-[#00FF88]/30 bg-[#00FF88]/5 flex flex-col items-center justify-center text-center animate-glow h-full" data-testid="solution-card">
                <Sparkles className="w-8 h-8 text-[#00FF88] mb-2" />
                <p className="text-sm font-bold text-[#00FF88] font-outfit">Jamadhan Fixes All</p>
                <p className="text-xs text-[#CBD5E1] mt-1">Join the system</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══ WHY CHOOSE JAMADHAN ═══ */}
      <section className="py-14 sm:py-20 px-4 sm:px-6 relative" data-testid="why-section">
        <div className="absolute inset-0 grid-terminal" />
        <div className="max-w-7xl mx-auto relative z-10">
          <Reveal className="text-center mb-12">
            <p className="font-jetbrains text-xs tracking-[0.25em] uppercase text-[#00FF88] mb-3">THE EDGE</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white font-outfit">Why Choose Jamadhan?</h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {whyUs.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.08}>
                <div className="terminal-card rounded-lg p-6 hover:border-[#00F0FF]/20 transition-all duration-300" data-testid={`why-card-${i}`}>
                  <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-[#00FF88]/10 to-[#00F0FF]/10 flex items-center justify-center mb-4">
                    <item.icon className="w-5 h-5 text-[#00FF88]" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-lg font-semibold text-white font-outfit mb-2">{item.title}</h3>
                  <p className="text-base text-[#CBD5E1] font-plex leading-relaxed">{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ COURSES ═══ */}
      <section id="courses" className="py-14 sm:py-20 px-4 sm:px-6 relative" data-testid="courses-section">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#00FF88]/[0.02] to-transparent" />
        <div className="max-w-7xl mx-auto relative z-10">
          <Reveal className="text-center mb-12">
            <p className="font-jetbrains text-xs tracking-[0.25em] uppercase text-[#00F0FF] mb-3">LEARN</p>
            <h2 className="text-4xl sm:text-5xl font-bold text-white font-outfit mb-4">Our Courses</h2>
            <p className="text-[#CBD5E1] max-w-xl mx-auto text-lg font-plex">From fundamentals to advanced option strategies — become a complete trader</p>
          </Reveal>
          <Reveal>
            <CourseCarousel courses={courses} />
          </Reveal>
        </div>
      </section>

      {/* ═══ COMPARISON ═══ */}
      <section className="py-24 px-4 sm:px-6 relative overflow-hidden" data-testid="comparison-section">
        {/* Subtle luxury ambient glows */}
        <div className="absolute top-1/2 left-1/4 -translate-x-1/2 -translate-y-1/2 w-[350px] md:w-[700px] h-[350px] md:h-[500px] bg-[#00FF88]/[0.06] blur-[120px] pointer-events-none rounded-full" />
        <div className="absolute top-1/2 right-1/4 translate-x-1/2 -translate-y-1/2 w-[350px] md:w-[700px] h-[350px] md:h-[500px] bg-[#00F0FF]/[0.06] blur-[120px] pointer-events-none rounded-full" />

        <div className="max-w-6xl mx-auto relative z-10">
          <Reveal className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full text-[11px] font-bold font-jetbrains uppercase tracking-[0.2em] mb-4 bg-white/[0.04] border border-white/[0.08] text-[#00FF88] shadow-[0_0_20px_rgba(0,255,136,0.15)]">
              <Sparkles className="w-3.5 h-3.5 text-[#00FF88]" />
              <span>THE INSTITUTIONAL ADVANTAGE</span>
            </div>
            <h2 className="text-3xl sm:text-5xl lg:text-6xl font-black text-white font-outfit mb-5 tracking-tight">
              Why Serious Traders<br />Choose <span className="text-gradient-neon">JamaDhan</span>
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto text-base sm:text-lg font-plex leading-relaxed">
              Compare transparently. We replaced inflated course fees, junior trainers, and generic theory with direct mentor-led institutional execution.
            </p>
          </Reveal>

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-0 items-stretch justify-center">

            {/* 🔴 Left: Other Institutes (Outdated Model) */}
            <Reveal className="w-full lg:w-[46%] flex flex-col bg-[#0A0D14]/90 backdrop-blur-xl rounded-3xl lg:rounded-r-none lg:rounded-l-3xl overflow-hidden border border-white/[0.06] shadow-2xl transition-all">
              {/* Header */}
              <div className="p-7 sm:p-8 pb-6 border-b border-white/[0.06] flex items-center justify-between bg-[#080A0F]">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl border border-red-500/20 bg-red-500/5 flex items-center justify-center shrink-0 shadow-[0_0_20px_rgba(239,68,68,0.1)]">
                    <X className="w-6 h-6 text-red-400" />
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-red-400/80 uppercase tracking-widest font-jetbrains">TRADITIONAL MODEL</span>
                    <h3 className="text-xl sm:text-2xl font-bold text-[#A1A1AA] font-outfit mt-0.5">Other Institutes</h3>
                  </div>
                </div>
                <span className="text-[11px] font-jetbrains text-gray-500 hidden sm:inline-block">High Cost · Low Depth</span>
              </div>

              {/* Rows */}
              <div className="p-7 sm:p-8 pt-6 flex-1 flex flex-col justify-between gap-5 text-sm font-medium text-[#71717A] bg-transparent">
                <div className="flex justify-between items-center border-b border-white/[0.04] pb-4">
                  <span className="text-gray-400 font-plex">Learning Mode</span>
                  <span className="text-right text-[#A1A1AA]">Recorded videos or batch-dependent</span>
                </div>
                <div className="flex justify-between items-center border-b border-white/[0.04] pb-4">
                  <span className="text-gray-400 font-plex">Workshop Fee</span>
                  <span className="text-right font-jetbrains text-red-400/90 font-medium">₹15,000 – ₹30,000</span>
                </div>
                <div className="flex justify-between items-center border-b border-white/[0.04] pb-4">
                  <span className="text-gray-400 font-plex">Mentorship</span>
                  <span className="text-right text-[#A1A1AA]">Junior instructors / varies by batch</span>
                </div>
                <div className="flex justify-between items-center border-b border-white/[0.04] pb-4">
                  <span className="text-gray-400 font-plex">Trading Psychology</span>
                  <span className="text-right text-[#A1A1AA]">Rarely covered or superficial</span>
                </div>
                <div className="flex justify-between items-center border-b border-white/[0.04] pb-4">
                  <span className="text-gray-400 font-plex">Class Recordings</span>
                  <span className="text-right text-[#A1A1AA]">Time-restricted / extra charges</span>
                </div>
                <div className="flex justify-between items-center border-b border-white/[0.04] pb-4">
                  <span className="text-gray-400 font-plex">Doubt Resolution</span>
                  <span className="text-right text-[#A1A1AA]">Slow forum / 48hr+ queue</span>
                </div>
                <div className="flex justify-between items-center pb-2">
                  <span className="text-gray-400 font-plex">Rating & Feedback</span>
                  <span className="text-right flex items-center justify-end gap-1.5 text-[#A1A1AA]">
                    4.8 <Star className="w-3.5 h-3.5 fill-[#71717A] text-[#71717A]" />
                    <span className="text-xs text-gray-500">(Mixed reviews)</span>
                  </span>
                </div>
              </div>
            </Reveal>

            {/* 🟢 Right: JamaDhan (Ultra-Luxury Institutional Standard) */}
            <Reveal delay={0.15} className="w-full lg:w-[54%] flex flex-col rounded-3xl overflow-hidden relative z-10 lg:-ml-2 transform lg:scale-[1.03] transition-all hover-lift">
              {/* Outer iridescent border container */}
              <div className="p-[1.5px] rounded-3xl bg-gradient-to-b from-[#00FF88] via-[#00F0FF] to-[#00FF88]/40 shadow-[0_0_60px_rgba(0,255,136,0.18)] flex-1 flex flex-col">
                <div className="bg-[#06080E]/95 backdrop-blur-2xl rounded-[calc(1.5rem-1px)] flex-1 flex flex-col overflow-hidden">
                  
                  {/* Top Luxury Banner */}
                  <div className="bg-gradient-to-r from-[#00FF88] via-[#00F0FF] to-[#00FF88] py-2.5 px-4 flex items-center justify-center gap-2 text-[#050505] text-xs font-black tracking-[0.2em] uppercase font-jetbrains shadow-[0_0_25px_rgba(0,240,255,0.4)]">
                    <Sparkles className="w-3.5 h-3.5 fill-black" />
                    <span>SMART CHOICE · INSTITUTIONAL EXCELLENCE</span>
                  </div>

                  {/* Header */}
                  <div className="p-7 sm:p-8 pb-6 border-b border-white/[0.08] flex items-center justify-between bg-gradient-to-b from-white/[0.04] to-transparent">
                    <div className="flex items-center gap-3.5">
                      <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-[#00FF88]/20 to-[#00F0FF]/20 border border-[#00FF88]/40 flex items-center justify-center shrink-0 shadow-[0_0_20px_rgba(0,255,136,0.3)]">
                        <img src="/jamadhan-logo.png" alt="Jamadhan Logo" className="w-7 h-7 object-contain" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-2xl sm:text-3xl font-black text-white font-outfit tracking-wide">JamaDhan</h3>
                          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold font-jetbrains bg-[#00FF88]/15 text-[#00FF88] border border-[#00FF88]/30">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#00FF88] animate-pulse" /> LIVE MENTORSHIP
                          </span>
                        </div>
                        <p className="text-xs text-[#94A3B8] font-plex mt-0.5">Built for Serious Wealth Creators</p>
                      </div>
                    </div>
                  </div>

                  {/* Rows */}
                  <div className="p-7 sm:p-8 pt-6 flex-1 flex flex-col justify-between gap-4 text-sm font-medium text-[#CBD5E1]">
                    
                    {/* Row 1: Learning Mode */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 border-b border-white/[0.07] pb-3.5 hover:bg-white/[0.02] rounded-xl px-2.5 transition-colors">
                      <span className="text-[#94A3B8] font-plex text-xs sm:text-sm">Learning Mode</span>
                      <div className="flex items-center gap-2 justify-between sm:justify-end">
                        <span className="text-white font-semibold text-right text-xs sm:text-sm">In-person (Jaipur) + Interactive Live PAN-India</span>
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold font-jetbrains bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30 shrink-0">HYBRID</span>
                      </div>
                    </div>

                    {/* Row 2: Fee */}
                    <div className="flex items-center justify-between border-b border-white/[0.07] pb-3.5 hover:bg-white/[0.02] rounded-xl px-2.5 transition-colors">
                      <span className="text-[#94A3B8] font-plex text-xs sm:text-sm">Workshop Fee</span>
                      <div className="flex items-center gap-2.5">
                        <span className="text-2xl font-black font-jetbrains text-[#00FF88] drop-shadow-[0_0_12px_rgba(0,255,136,0.4)]">FREE</span>
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold font-jetbrains bg-[#00FF88]/15 text-[#00FF88] border border-[#00FF88]/30 shrink-0">100% FREE</span>
                      </div>
                    </div>

                    {/* Row 3: Mentor - DEEPAK SETHIA */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 border-b border-white/[0.07] pb-3.5 hover:bg-white/[0.02] rounded-xl px-2.5 transition-colors">
                      <span className="text-[#94A3B8] font-plex text-xs sm:text-sm">Direct Mentor</span>
                      <div className="flex items-center gap-2 justify-between sm:justify-end">
                        <span className="text-white font-bold text-xs sm:text-sm">Deepak Sethia <span className="text-[#00F0FF] font-normal">• 12+ Years Pro</span></span>
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold font-jetbrains bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/30 shrink-0">FOUNDER</span>
                      </div>
                    </div>

                    {/* Row 4: Trading Psychology */}
                    <div className="flex items-center justify-between border-b border-white/[0.07] pb-3.5 hover:bg-white/[0.02] rounded-xl px-2.5 transition-colors">
                      <span className="text-[#94A3B8] font-plex text-xs sm:text-sm">Trading Psychology</span>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-semibold text-xs sm:text-sm">Institutional Discipline & Risk Rule</span>
                        <CheckCircle className="w-4 h-4 text-[#00FF88] drop-shadow-[0_0_8px_rgba(0,255,136,0.6)] shrink-0" />
                      </div>
                    </div>

                    {/* Row 5: Class Recordings */}
                    <div className="flex items-center justify-between border-b border-white/[0.07] pb-3.5 hover:bg-white/[0.02] rounded-xl px-2.5 transition-colors">
                      <span className="text-[#94A3B8] font-plex text-xs sm:text-sm">Class Recordings</span>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-semibold text-xs sm:text-sm">Lifetime HD Portal Access</span>
                        <CheckCircle className="w-4 h-4 text-[#00FF88] drop-shadow-[0_0_8px_rgba(0,255,136,0.6)] shrink-0" />
                      </div>
                    </div>

                    {/* Row 6: Doubt Sessions */}
                    <div className="flex items-center justify-between border-b border-white/[0.07] pb-3.5 hover:bg-white/[0.02] rounded-xl px-2.5 transition-colors">
                      <span className="text-[#94A3B8] font-plex text-xs sm:text-sm">Doubt Resolution</span>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-semibold text-xs sm:text-sm">1-on-1 Direct Support with Deepak Sir</span>
                        <CheckCircle className="w-4 h-4 text-[#00FF88] drop-shadow-[0_0_8px_rgba(0,255,136,0.6)] shrink-0" />
                      </div>
                    </div>

                    {/* Row 7: Google Rating */}
                    <div className="flex items-center justify-between pb-3 hover:bg-white/[0.02] rounded-xl px-2.5 transition-colors">
                      <span className="text-[#94A3B8] font-plex text-xs sm:text-sm">Google Rating</span>
                      <div className="flex items-center gap-2 font-jetbrains">
                        <span className="text-[#FFCC00] font-black text-sm sm:text-base flex items-center gap-1">
                          4.9 <Star className="w-4 h-4 fill-[#FFCC00] text-[#FFCC00]" />
                        </span>
                        <span className="text-xs text-gray-400 font-plex">(100+ Verified Reviews)</span>
                        <CheckCircle className="w-4 h-4 text-[#00FF88] drop-shadow-[0_0_8px_rgba(0,255,136,0.6)] shrink-0" />
                      </div>
                    </div>

                    {/* CTA Button */}
                    <div className="pt-2">
                      <Button
                        onClick={() => navigate('/login')}
                        className="w-full h-12 rounded-2xl bg-gradient-to-r from-[#00FF88] via-[#00F0FF] to-[#00FF88] text-black font-black font-outfit text-base shadow-[0_0_30px_rgba(0,255,136,0.35)] hover:shadow-[0_0_50px_rgba(0,255,136,0.55)] hover:scale-[1.01] transition-all duration-300 group"
                      >
                        <span>Join Next Workshop For Free</span>
                        <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1.5 transition-transform" />
                      </Button>
                      <p className="text-center text-[11px] text-gray-400 font-plex mt-2">
                        ⚡ Limited seats per batch · 100% Free Access
                      </p>
                    </div>

                  </div>
                </div>
              </div>
            </Reveal>

          </div>
        </div>
      </section>

      {/* ═══ WHAT WE OFFER TO STUDENTS (COVERFLOW SLIDER) ═══ */}
      <StudentOfferingsCarousel onExploreClick={() => navigate('/login')} />

      {/* ═══ ABOUT / TIMELINE ═══ */}
      <section id="about" className="py-20 px-6 relative" data-testid="about-section">
        <CandlestickBg opacity={0.02} />
        <div className="max-w-4xl mx-auto relative z-10">
          <Reveal className="text-center mb-14">
            <p className="font-jetbrains text-xs tracking-[0.25em] uppercase text-[#00FF88] mb-3">OUR STORY</p>
            <h2 className="text-4xl sm:text-5xl font-bold text-white font-outfit mb-4">The Jamadhan Journey</h2>
            <p className="text-[#CBD5E1] max-w-xl mx-auto text-lg font-plex">From a trader who lost everything to building India's most trusted trading education system</p>
          </Reveal>

          {/* Timeline */}
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#00FF88] via-[#00F0FF] to-[#00FF88] opacity-30" />

            {timeline.map((item, i) => (
              <Reveal key={item.year} delay={i * 0.15}>
                <div className={`relative flex items-start gap-6 mb-10 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} md:text-${i % 2 === 0 ? 'right' : 'left'}`}>
                  {/* Dot */}
                  <div className="absolute left-4 md:left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-[#00FF88] shadow-[0_0_15px_rgba(0,255,136,0.5)] z-10" style={{ top: '6px' }} />

                  {/* Content */}
                  <div className={`ml-12 md:ml-0 md:w-[calc(50%-2rem)] ${i % 2 === 0 ? 'md:pr-8' : 'md:pl-8'}`}>
                    <span className="font-jetbrains text-xs tracking-[0.2em] text-[#00F0FF]">{item.year}</span>
                    <h3 className="text-xl font-bold text-white font-outfit mt-1 mb-2">{item.title}</h3>
                    <p className="text-base text-[#CBD5E1] font-plex leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Mission / Vision */}
          <Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-12">
              <div className="terminal-card rounded-lg p-6" data-testid="mission-card">
                <p className="font-jetbrains text-xs tracking-[0.2em] text-[#00FF88] mb-2">MISSION</p>
                <p className="text-base text-[#CBD5E1] font-plex leading-relaxed">To make institutional-grade trading education accessible to every aspiring trader in India, eliminating the 90% failure rate through structured learning and AI-powered tools.</p>
              </div>
              <div className="terminal-card rounded-lg p-6" data-testid="vision-card">
                <p className="font-jetbrains text-xs tracking-[0.2em] text-[#00F0FF] mb-2">VISION</p>
                <p className="text-base text-[#CBD5E1] font-plex leading-relaxed">To become India's #1 AI-powered trading education ecosystem — where learning meets live market intelligence, producing consistently profitable traders.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ LIVE MARKET OPPORTUNITIES ═══ */}
      <section id="drishti-preview" className="py-14 sm:py-20 px-4 sm:px-6 relative" data-testid="live-market-section">
        <div className="absolute inset-0 bg-market" />
        <Particles />
        <div className="max-w-7xl mx-auto relative z-10">
          <Reveal className="text-center mb-12">
            <p className="font-jetbrains text-xs tracking-[0.25em] uppercase text-[#00F0FF] mb-3">LIVE MARKET SCANNER</p>
            <h2 className="text-4xl sm:text-5xl font-bold text-white font-outfit">Drishti AI Opportunities</h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {liveOpportunities.map((opp, i) => (
              <Reveal key={opp.title} delay={i * 0.1}>
                <div className="tracing-card rounded-lg p-6 group cursor-pointer" style={{ '--glow': `${opp.color}30` }} data-testid={`market-card-${i}`}>
                  <div className="flex items-center justify-between mb-4">
                    <opp.icon className="w-5 h-5" style={{ color: opp.color }} />
                    <span className="font-jetbrains text-2xl font-bold" style={{ color: opp.color }}>{opp.count}</span>
                  </div>
                  <h3 className="text-base font-semibold text-white font-outfit mb-1">{opp.title}</h3>
                  <p className="text-sm text-[#94A3B8] font-plex">{opp.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {(() => {
        const videoReviews = [
          { video: "/review_video_1.mp4", name: reviews[0]?.student_name || "Aakash Gupta", thumb: reviews[0]?.thumbnail || "https://jamadhan.com/wp-content/uploads/2024/07/jamadhan-student-Akash-Gupta.png.webp" },
          { video: "/review_video_2.mp4", name: reviews[1]?.student_name || "Uday Verma", thumb: reviews[1]?.thumbnail || "https://jamadhan.com/wp-content/uploads/2024/06/1-1.png" },
          { video: "/review_video_3.mp4", name: reviews[2]?.student_name || "Gaurav Prajapat", thumb: reviews[2]?.thumbnail || "https://jamadhan.com/wp-content/uploads/2024/06/2-1.png" },
          { video: "/review_video_4.mp4", name: reviews[3]?.student_name || "Devanshu Gupta", thumb: reviews[3]?.thumbnail || "https://jamadhan.com/wp-content/uploads/2024/07/jamadhan-student-Devanshu-Gupta.png.webp" }
        ];

        return (
          <section id="reviews" className="py-20 px-6 relative" data-testid="reviews-section">
            <style dangerouslySetInnerHTML={{
              __html: `
              .video-review-card {
                position: relative;
                background: rgba(12, 12, 16, 0.6);
                border-radius: 16px;
                overflow: hidden;
                transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
                border: 1px solid rgba(168, 85, 247, 0.2);
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
                aspect-ratio: 3/4;
              }
              .video-review-card::before {
                content: '';
                position: absolute;
                inset: 0;
                border-radius: 16px;
                padding: 1.5px;
                background: linear-gradient(135deg, rgba(168, 85, 247, 0.3), rgba(59, 130, 246, 0.3));
                -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
                -webkit-mask-composite: xor;
                mask-composite: exclude;
                pointer-events: none;
                transition: all 0.4s ease;
              }
              .video-review-card:hover {
                transform: scale(1.07);
                z-index: 10;
                border-color: transparent;
                box-shadow: 0 12px 30px rgba(168, 85, 247, 0.2);
              }
              .video-review-card:hover::before {
                background: linear-gradient(135deg, rgba(168, 85, 247, 0.8), rgba(59, 130, 246, 0.8));
              }
              
              .testimonial-text {
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                overflow: hidden;
                text-overflow: ellipsis;
                max-height: 3rem;
                transition: max-height 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
              }
              .review-card-container:hover .testimonial-text {
                display: block;
                -webkit-line-clamp: unset;
                max-height: 25rem;
              }
            `}} />
            <Particles />
            <div className="max-w-7xl mx-auto relative z-10">
              <Reveal className="text-center mb-12">
                <p className="font-jetbrains text-xs tracking-[0.25em] uppercase text-[#FFCC00] mb-3">PROOF</p>
                <h2 className="text-4xl sm:text-5xl font-bold text-white font-outfit">What Our Traders Say</h2>
              </Reveal>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
                {videoReviews.map((v, i) => (
                  <Reveal key={i} delay={i * 0.05}>
                    <VideoCard
                      videoSrc={v.video}
                      studentName={v.name}
                      studentThumb={v.thumb}
                    />
                  </Reveal>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {(() => {
                  const fallbackTextReviews = [
                    {
                      review_id: "fb1",
                      student_name: "Aakash Gupta",
                      course: "Technical Analysis Course",
                      rating: 5,
                      profit_achieved: "Deep stock analysis skills",
                      testimonial: "I've done basic course from here till now, It's actually great learning for me, deep analysis of stocks, stock selection, perfect entry-exit. I've learned..."
                    },
                    {
                      review_id: "fb2",
                      student_name: "Uday Verma",
                      course: "Super Trader Edge Course",
                      rating: 5,
                      profit_achieved: "Complete trading knowledge",
                      testimonial: "The instructors at Jama Dhan stock market institute are top-notch! Their deep knowledge and practical insights into financial markets have been invaluable...."
                    }
                  ];
                  const displayReviews = reviews.length > 0 ? reviews : fallbackTextReviews;
                  return displayReviews.slice(0, showAllReviews ? undefined : 2).map((rev, i) => (
                    <Reveal key={rev.review_id} delay={i * 0.05}>
                      <div className="terminal-card review-card-container rounded-lg p-6 hover:border-[#00FF88]/20 transition-all cursor-pointer" data-testid={`review-card-${i}`}>
                        <div className="flex items-start gap-4 mb-4">
                          <img
                            src={rev.thumbnail}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = '/jamadhan-logo.png';
                              e.target.style.objectFit = 'contain';
                              e.target.style.padding = '6px';
                              e.target.style.backgroundColor = '#000';
                            }}
                            alt={rev.student_name}
                            className="w-11 h-11 rounded-full object-cover border border-white/10"
                          />
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-white font-outfit">{rev.student_name}</h4>
                            <p className="text-xs text-[#94A3B8] font-plex">{rev.course}</p>
                            <div className="flex gap-0.5 mt-1">{Array.from({ length: rev.rating }).map((_, si) => <Star key={si} className="w-3 h-3 text-[#FFCC00] fill-[#FFCC00]" />)}</div>
                          </div>
                          {rev.profit_achieved && <Badge className="bg-[#00FF88]/15 text-[#00FF88] border-0 text-xs font-jetbrains shrink-0">{rev.profit_achieved}</Badge>}
                        </div>
                        <div className="review-text-container">
                          <p className="text-base text-[#CBD5E1] font-plex leading-relaxed italic testimonial-text">
                            "{rev.testimonial}"
                          </p>
                        </div>
                      </div>
                    </Reveal>
                  ));
                })()}
              </div>

              {reviews.length > 2 && (
                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => setShowAllReviews(!showAllReviews)}
                    className="flex items-center gap-2 px-5 py-2.5 rounded border border-[#00FF88]/20 bg-[#00FF88]/10 text-[#00FF88] hover:bg-[#00FF88]/20 font-jetbrains text-sm font-semibold transition-all shadow-[0_0_15px_rgba(0,255,136,0.1)]"
                  >
                    {showAllReviews ? (
                      <>View Less <span className="text-xs">↑</span></>
                    ) : (
                      <>View More <span className="text-xs">→</span></>
                    )}
                  </button>
                </div>
              )}
            </div>
          </section>
        );
      })()}

      {/* ═══ BLOG PREVIEW ═══ */}
      {blogs.length > 0 && (
        <section className="py-20 px-6 relative" data-testid="blog-preview-section">
          <div className="absolute inset-0 grid-terminal opacity-50" />
          <div className="max-w-7xl mx-auto relative z-10">
            <Reveal className="flex items-center justify-between mb-10">
              <div>
                <p className="font-jetbrains text-xs tracking-[0.25em] uppercase text-[#00F0FF] mb-2">INSIGHTS</p>
                <h2 className="text-3xl sm:text-4xl font-bold text-white font-outfit">Latest from Blog</h2>
              </div>
              <Link to="/blog"><Button variant="outline" className="border-white/10 text-[#CBD5E1] hover:bg-white/5 rounded-sm hidden sm:flex">View All <ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {blogs.slice(0, 3).map((blog, i) => (
                <Reveal key={blog.blog_id} delay={i * 0.1}>
                  <Link to={`/blog/${blog.slug}`} className="tracing-card rounded-lg overflow-hidden block group" data-testid={`blog-preview-${i}`}>
                    {blog.cover_image && <div className="h-36 overflow-hidden"><img src={blog.cover_image} alt={blog.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>}
                    <div className="p-5">
                      <div className="flex gap-1 mb-2">{(blog.tags || []).slice(0, 2).map((t, ti) => <Badge key={ti} className="bg-[#00FF88]/10 text-[#00FF88] border-0 text-xs rounded-sm">{t}</Badge>)}</div>
                      <h3 className="text-base font-semibold text-white font-outfit mb-1 group-hover:text-[#00FF88] transition-colors line-clamp-2">{blog.title}</h3>
                      <p className="text-sm text-[#94A3B8] font-plex line-clamp-2">{blog.excerpt}</p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══ TELEGRAM CTA ═══ */}
      <section className="py-16 px-6" data-testid="telegram-section">
        <Reveal>
          <div className="max-w-4xl mx-auto terminal-card rounded-lg p-8 flex flex-col md:flex-row items-center gap-6 border-[#00F0FF]/20">
            <div className="w-14 h-14 rounded-full bg-[#0088cc]/10 flex items-center justify-center shrink-0 border border-[#0088cc]/20">
              <svg viewBox="0 0 24 24" className="w-7 h-7 text-[#0088cc]" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" /></svg>
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-xl font-semibold text-white font-outfit mb-1">{SC.telegram_cta_text || "Join 10000+ Traders on Telegram"}</h3>
              <p className="text-base text-[#94A3B8] font-plex">Daily market updates, trade setups, and community discussions</p>
            </div>
            <Button onClick={() => window.open(TELE, "_blank")} className="bg-[#0088cc] text-white hover:brightness-110 rounded-sm" data-testid="telegram-btn">
              Join Now <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </Reveal>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="py-24 px-6 relative" data-testid="cta-section">
        <Particles />
        <Reveal>
          <div className="max-w-3xl mx-auto text-center relative z-10">
            <p className="font-jetbrains text-xs tracking-[0.3em] uppercase text-[#CBD5E1] mb-4">READY?</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white font-outfit mb-6">
              Start Your<br /><span className="text-gradient-neon">Transformation</span> Today
            </h2>
            <p className="text-[#CBD5E1] mb-8 max-w-xl mx-auto text-base font-plex">
              Join the system that has turned complete beginners into profitable traders. Your first course is <span className="text-[#00FF88] font-semibold">FREE</span>.
            </p>
            <Button onClick={() => navigate('/login')} size="lg" className="bg-[#00FF88] text-black font-bold hover:bg-black hover:text-white hover:shadow-[0_0_40px_rgba(0,255,136,0.3)] border border-transparent hover:border-[#00FF88] rounded-sm px-10 text-base transition-all duration-200" data-testid="cta-btn">
              {SC.begin_now_cta || "Begin Now — It's Free"} <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </Reveal>
      </section>

      {/* ═══ FAQ SECTION ═══ */}
      <section className="py-20 px-6 relative" data-testid="faq-section">
        <div className="max-w-3xl mx-auto relative z-10">
          <Reveal className="text-center mb-12">
            <p className="font-jetbrains text-xs tracking-[0.25em] uppercase text-[#00FF88] mb-3">
              {SC.home_faq_eyebrow || "GOT QUESTIONS?"}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white font-outfit">
              {SC.home_faq_heading || "Frequently Asked Questions"}
            </h2>
          </Reveal>
          <div className="space-y-3">
            {(() => {
              const dynamicFaqs = [];
              for (let i = 1; i <= 10; i++) {
                const q = SC[`home_faq_${i}_q`];
                const a = SC[`home_faq_${i}_a`];
                if (q && a) {
                  dynamicFaqs.push({ q, a });
                }
              }
              const activeFaqs = dynamicFaqs.length > 0 ? dynamicFaqs : [
                {
                  q: "I have never done any trade in stock market, Can I Join now?",
                  a: "At Jamadhan, we have courses for people with all levels of knowledge, from those who are just starting out to those who are now experts. If you're new to the stock market, you can start with our introductory classes to learn the basics of how it works. Our courses include a wide range of topics, such as technical analysis, trading options, trading stocks, trading foreign currencies, trading commodities, and more. Our skilled teachers teach you everything you need to know to help you reach your investment goals. Join us today and take the first step towards mastering the stock market."
                },
                {
                  q: "How we clarify our doubts after live class?",
                  a: "At Jamadhan, we know how important it is for our students to have a personal mentor and help. So, we give each student a mentor, who will be there to answer any concerns or doubts you might have. Our trainers are professionals with a lot of expertise who know a lot about the stock market and how to invest. We also give you access to recordings of the courses, so you can go over the content at your own pace and when it suits you. Join us today and we'll help you do well on the stock market with personalised advice and aid whenever you need it."
                },
                {
                  q: "Is your technical study applied only on equity?",
                  a: "Our strategy is universal, which means you may use it with any asset class (stocks, commodities, or foreign exchange)."
                },
                {
                  q: "What's the probability I can expect?",
                  a: "It's up to the person. You need a lot of practise and patience to get better. After a while of practice, your aim will start to get better."
                },
                {
                  q: "How much gain I can expect after completion of course?",
                  a: "It depends on how well you comprehend the ideas, how well you can control your emotions, and how much experience you have. It also relies on how eager you are to take risks. You might get anywhere from 2% to 100% a month, depending on how much risk you are willing to take."
                }
              ];
              return activeFaqs.map((faq, idx) => (
                <FaqItem key={idx} q={faq.q} a={faq.a} />
              ));
            })()}
          </div>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="relative overflow-hidden border-t border-white/[0.06] bg-[#030305] px-6 pb-8 pt-14" data-testid="footer">
        <div className="absolute inset-0 grid-terminal opacity-25" />
        <div className="absolute left-0 top-0 h-64 w-64 rounded-full bg-[#00FF88]/5 blur-3xl" />
        <div className="absolute right-0 bottom-0 h-72 w-72 rounded-full bg-[#00F0FF]/5 blur-3xl" />

        <div className="relative z-10 mx-auto max-w-7xl">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <div className="mb-7 flex items-center gap-3">
                <div className="h-10 w-10 overflow-hidden rounded shadow-[0_0_18px_rgba(0,255,136,0.28)]">
                  <img src="/jamadhan-logo.png" alt="Jama Dhan" className="h-full w-full object-cover" />
                </div>
                <span className="font-outfit text-2xl font-black tracking-tight text-white">Jama Dhan</span>
              </div>
              <ul className="space-y-5 text-sm leading-7 text-white">
                <li className="flex gap-3">
                  <MapPin className="mt-1 h-4 w-4 shrink-0 text-[#00FF88]" />
                  <span>3rd Floor, JamaDhan Stock Market Institute, Ajmer Road, Opposite MG Motors, Heera Nagar, Tagore Nagar, Jaipur, Rajasthan, 302021</span>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="h-4 w-4 shrink-0 text-[#00FF88]" />
                  <a href="tel:+919257059361" className="transition-colors hover:text-[#00FF88]">+91-9257059361</a>
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="h-4 w-4 shrink-0 text-[#00FF88]" />
                  <a href="mailto:contactus@jamadhan.com" className="transition-colors hover:text-[#00FF88]">contactus@jamadhan.com</a>
                </li>
              </ul>
              <div className="mt-7 flex items-center gap-2">
                {[
                  { label: "WhatsApp", href: "https://wa.me/919257059361", color: "text-[#25D366]", svg: <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor"><path d="M20.52 3.48A11.82 11.82 0 0 0 12.08 0C5.54 0 .22 5.32.22 11.86c0 2.09.55 4.13 1.59 5.93L.12 24l6.36-1.67a11.82 11.82 0 0 0 5.6 1.43h.01c6.54 0 11.86-5.32 11.86-11.86 0-3.17-1.23-6.15-3.43-8.42ZM12.09 21.76h-.01a9.83 9.83 0 0 1-5.01-1.37l-.36-.22-3.77.99 1.01-3.67-.24-.38a9.84 9.84 0 0 1-1.5-5.25C2.21 6.42 6.64 2 12.08 2c2.64 0 5.11 1.03 6.98 2.89a9.77 9.77 0 0 1 2.89 6.97c0 5.45-4.43 9.9-9.86 9.9Zm5.4-7.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.38-1.46-.88-.78-1.47-1.75-1.64-2.04-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.49s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.08 4.48.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2-1.41.25-.69.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35Z" /></svg> },
                  { label: "YouTube", href: "https://www.youtube.com/@jamadhan", color: "text-[#FF0000]", icon: <Youtube className="h-4 w-4" /> },
                  { label: "Instagram", href: "https://www.instagram.com/jama_dhan/", color: "text-[#FF4D8D]", icon: <Instagram className="h-4 w-4" /> },
                  { label: "Facebook", href: "https://www.facebook.com/jamadhaninstitute", color: "text-[#1877F2]", icon: <Facebook className="h-4 w-4" /> },
                  { label: "Telegram", href: TELE, color: "text-[#26A5E4]", svg: <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0h-.056zm4.962 7.224c.451-.01.636.37.656.605.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.014-.15-.056-.212-.305-.188-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" /></svg> },
                ].map((social) => (
                  <a key={social.label} href={social.href} target="_blank" rel="noopener noreferrer" aria-label={social.label} className={`flex h-9 w-9 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.04] ${social.color} transition-all hover:-translate-y-1 hover:border-[#00FF88]/30 hover:bg-white/[0.08]`}>
                    {social.svg || social.icon}
                  </a>
                ))}
              </div>
            </div>

            <div>
              <h4 className="mb-6 font-outfit text-xl font-bold text-white">Quick Links</h4>
              <ul className="space-y-3">
                {[
                  { label: "Home", action: () => window.scrollTo({ top: 0, behavior: "smooth" }) },
                  { label: "About us", href: "/about/" },
                  { label: "Gallery", href: "/gallery/" },
                  { label: "Blogs", href: "/blog/" },
                  { label: "Contact us", href: "/contact/" },
                ].map((item) => (
                  <li key={item.label} className="flex items-center gap-2 text-[#A1A1AA]">
                    <ChevronRight className="h-4 w-4 text-white/55" />
                    {item.href ? (
                      item.href.startsWith("mailto") ? <a href={item.href} className="transition-colors hover:text-[#00FF88]">{item.label}</a> : <Link to={item.href} className="transition-colors hover:text-[#00FF88]">{item.label}</Link>
                    ) : (
                      <button type="button" onClick={item.action} className="transition-colors hover:text-[#00FF88]">{item.label}</button>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="mb-6 font-outfit text-xl font-bold text-white">Trending Courses</h4>
              <ul className="space-y-3">
                {(courses.length > 0 ? courses.slice(1, 4) : [
                  { title: "Super Trader Edge Course", course_id: "technical-analysis-course", slug: "technical-analysis-course" },
                  { title: "Super Option Analysis Course", course_id: "options-trading-course", slug: "options-trading-course" },
                  { title: "Super Trader Combo", course_id: "technical-analysis-options-combo-course", slug: "technical-analysis-options-combo-course" },
                ]).map((course) => (
                  <li key={course.course_id || course.title} className="flex items-start gap-2 text-[#A1A1AA]">
                    <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-white/55" />
                    <Link to={`/courses/${course.slug || course.course_id}/`} className="leading-6 transition-colors hover:text-[#00FF88]">
                      {course.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="mb-6 font-outfit text-xl font-bold text-white">Legal</h4>
              <ul className="space-y-3">
                {[
                  { label: "Privacy Policy", href: "/privacy-policy/" },
                  { label: "Terms & Conditions", href: "/terms-and-conditions/" },
                  { label: "Refund & Cancellation", href: "/refund-and-cancellation-policy/" },
                ].map((item) => (
                  <li key={item.href} className="flex items-center gap-2 text-[#A1A1AA]">
                    <ChevronRight className="h-4 w-4 text-white/55" />
                    <Link to={item.href} className="transition-colors hover:text-[#00FF88]">{item.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-12 border-y border-white/[0.10] py-5">
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-3 text-sm font-semibold text-white">
              {[
                { label: "Jaipur", href: "/stock-market-course-in-jaipur/" },
                { label: "Jodhpur", href: "/stock-market-classes-in-jodhpur/" },
                { label: "Udaipur", href: "/stock-market-classes-in-udaipur/" },
                { label: "Kota", href: "/stock-market-classes-in-kota/" },
                { label: "Ajmer", href: "/stock-market-classes-in-ajmer/" },
                { label: "Sikar", href: "/stock-market-classes-in-sikar/" },
                { label: "Bhilwara", href: "/stock-market-classes-in-bhilwara/" },
              ].map((city, index) => (
                <div key={city.label} className="flex items-center gap-4">
                  {index > 0 && <span className="hidden text-white/40 sm:inline">|</span>}
                  <Link to={city.href} className="transition-colors hover:text-[#00FF88]">{city.label}</Link>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col items-center justify-between gap-3 pt-6 md:flex-row">
            <p className="text-center text-xs text-[#94A3B8] md:text-left">&copy; {new Date().getFullYear()} {SC.footer_copyright || "Jama Dhan Stock Market Institute. All rights reserved."}</p>
            <a href="https://jamadhan.com" target="_blank" rel="noopener noreferrer" className="text-xs text-[#94A3B8] transition-colors hover:text-[#00FF88]">jamadhan.com</a>
          </div>

          {/* Regulatory & Risk Disclaimer */}
          <div className="mt-6 pt-4 border-t border-white/[0.04] text-[11px] leading-relaxed text-zinc-500 text-center sm:text-left">
            <p>
              JamaDhan is an educational institute that provides stock market training only. We are not registered with SEBI as an investment adviser or research analyst, and nothing on this website, in our classes or in our community groups is investment advice or a recommendation to buy, sell or hold any security. Trading and investing in securities, derivatives, commodities and currencies involve a high risk of loss, and securities market investments are subject to market risks. Examples shown are for teaching purposes only. Past performance and individual student experiences are not indicative of future results.
            </p>
          </div>
        </div>
      </footer>

      {/* ═══ FLOATING WHATSAPP BUTTON (BOTTOM RIGHT) ═══ */}
      <div className="fixed bottom-6 right-6 z-50 flex items-center">
        <a 
          href={`https://wa.me/${(SC.contact_phone || "919257059361").replace(/[^0-9]/g, '')}?text=Hi%20JamaDhan,%20I%20want%20to%20know%20more%20about%20your%20trading%20programs.`}
          target="_blank" 
          rel="noopener noreferrer"
          aria-label="Chat on WhatsApp"
          className="w-13 h-13 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-lg hover:scale-110 active:scale-95 transition-all duration-300 border-2 border-white/20 animate-pulse-slow hover:animate-none"
          style={{ width: "52px", height: "52px" }}
        >
          <svg viewBox="0 0 24 24" className="w-7 h-7" fill="currentColor">
            <path d="M20.52 3.48A11.82 11.82 0 0 0 12.08 0C5.54 0 .22 5.32.22 11.86c0 2.09.55 4.13 1.59 5.93L.12 24l6.36-1.67a11.82 11.82 0 0 0 5.6 1.43h.01c6.54 0 11.86-5.32 11.86-11.86 0-3.17-1.23-6.15-3.43-8.42ZM12.09 21.76h-.01a9.83 9.83 0 0 1-5.01-1.37l-.36-.22-3.77.99 1.01-3.67-.24-.38a9.84 9.84 0 0 1-1.5-5.25C2.21 6.42 6.64 2 12.08 2c2.64 0 5.11 1.03 6.98 2.89a9.77 9.77 0 0 1 2.89 6.97c0 5.45-4.43 9.9-9.86 9.9Zm5.4-7.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.38-1.46-.88-.78-1.47-1.75-1.64-2.04-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.49s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.08 4.48.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2-1.41.25-.69.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35Z" />
          </svg>
        </a>
      </div>
    </div>
  );
};

export default LandingPage;
